import { CommonModule } from '@angular/common';
import { Component, Injector, OnInit } from '@angular/core';
import { RouterModule, Router } from '@angular/router';
import { NgxPaginationModule } from 'ngx-pagination';
import {DEBOUNCE_TIME, extractErrorMessage, goToNextPage} from 'src/app/common/common';
import { RidViewModel } from 'src/app/model/rids.model';
import { AuthService } from 'src/app/services/auth.service';
import {ApiService, CustomHttpParams} from 'src/app/services/api.service';
import { FormsModule } from '@angular/forms';
import {PaginationClass, PaginationModel} from "../../../../common/pagination";
import {HttpParams} from "@angular/common/http";
import {BehaviorSubject, Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged, takeUntil} from 'rxjs/operators';

@Component({
  selector: "app-rid",
  standalone: true,
  imports: [RouterModule, CommonModule, NgxPaginationModule, FormsModule],
  templateUrl: "./rid-index.component.html",
  styleUrl: "./rid-index.component.scss",
})
export class RidIndexComponent implements OnInit {
  // Search fields
  public idSearch: string = '';
  public externalIdSearch: string = '';
  public dataSourceSearch: string = '';
  public marketContextSearch: string = '';
  public resourceIdSearch: string = '';
  public sampleRateMinSearch: string = '';
  public sampleRateMaxSearch: string = '';
  public powerTypeSearch: string = '';

  // Debounce search using BehaviorSubject
  private searchParams$ = new BehaviorSubject<any>({
    id: '',
    externalId: '',
    data_source: '',
    market_context: '',
    sampleRateMin: '',
    sampleRateMax: '',
    powerType: '',
  });
  private unsubscribe = new Subject<void>();

  protected readonly paginationId = 'ridsPagination';
  private pagination = new PaginationClass(this.paginationId);
  protected paginationModel: PaginationModel = this.pagination.getPaginationModel();

  public error: string | null = null;
  protected isLoading: boolean = false;
  public isAdmin: boolean = false;
  public displayedRids: RidViewModel[] = [];

  public router: Router;
  public params = new HttpParams();

  constructor(
    private injector: Injector,
    private authService: AuthService,
    private ridsService: ApiService
  ) {
    this.router = injector.get(Router);
  }

  ngOnInit(): void {
    this.isAdmin = this.authService.checkAuthorization();
    this.paginationModel = this.pagination.resetPagination();

    // Setup debounced search
    this.searchParams$.pipe(
      debounceTime(DEBOUNCE_TIME),
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
      takeUntil(this.unsubscribe)
    ).subscribe(() => {
      this.fetchRids();
    });
  }

  fetchRids() {
    this.isLoading = true;
    this.error = null;

    // Reset pagination when searching
    if (this.params.has('id') || this.params.has('external_id') ||
      this.params.has('data_source') || this.params.has('market_context') ||
      this.params.has('resource_id') || this.params.has('sampleRateMin') ||
      this.params.has('sampleRateMax')) {
      this.paginationModel = this.pagination.resetPagination();
    }

    // Build parameters
    const queryParams = this.buildSearchParams();
    this.ridsService.getRids(queryParams).subscribe({
      next: (response: RidViewModel[]) => {
        this.displayedRids = response.map((rid: RidViewModel) => rid);
        this.paginationModel = this.pagination.updateTotalItems(response.length);
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error loading RIDs:', err);
        this.error = extractErrorMessage(err);
        this.isLoading = false;
      }
    });
  }

  buildSearchParams(): CustomHttpParams {
    const params: CustomHttpParams = {
      'depths': {
        'power_type': 1,
        'metadata_reports': 0,
        'registered_reports': 0,
        'mrids': 0
      },
      'offset': this.pagination.offset,
      'limit': this.pagination.itemsPerPage,
    };

    // Add search parameters if they exist
    if (this.idSearch) {
      params['id'] = this.idSearch.trim();
    }

    if (this.externalIdSearch) {
      params['external_id'] = this.externalIdSearch.trim();
    }

    if (this.dataSourceSearch) {
      params['data_source'] = this.dataSourceSearch.trim();
    }

    if (this.marketContextSearch) {
      params['market_context'] = this.marketContextSearch.trim();
    }

    if (this.resourceIdSearch) {
      params['resource_id'] = this.resourceIdSearch.trim();
    }

    if (this.sampleRateMinSearch) {
      params['sampleRateMin'] = this.sampleRateMinSearch.trim();
    }

    if (this.sampleRateMaxSearch) {
      params['sampleRateMax'] = this.sampleRateMaxSearch.trim();
    }

    if (this.powerTypeSearch) {
      params['power_type.itemLabel'] = this.powerTypeSearch.trim();
    }
    return params;
  }

  triggerSearch() {
    this.paginationModel = this.pagination.resetPagination();

    // Update the search parameters subject with current values
    this.searchParams$.next({
      id: this.idSearch,
      external_id: this.externalIdSearch,
      data_source: this.dataSourceSearch,
      market_context: this.marketContextSearch,
      resource_id: this.resourceIdSearch,
      sampleRateMin: this.sampleRateMinSearch,
      sampleRateMax: this.sampleRateMaxSearch,
      power_type: this.powerTypeSearch,
    });
  }

  handlePageChange(newPage: number) {
    this.paginationModel = this.pagination.changePage(newPage);
    this.fetchRids();
  }

  /**
   * Refresh RIDs - reload current page
   */
  refreshRids() {
    this.fetchRids();
  }

  /**
   * Navigation helper
   */
  nextPage(event: any, url: string, router: Router) {
    return goToNextPage(event, url, router);
  }

  /**
   * Delete RID with confirmation
   */
  delete(rid: any) {
    if (confirm('Are you sure you want to delete this RID?')) {
      this.isLoading = true;
      this.ridsService.deleteRids(rid.id).subscribe({
        next: () => {
          this.fetchRids();
        },
        error: (err) => {
          console.error(err);
          this.error = extractErrorMessage(err);
          this.isLoading = false;
        }
      });
    }
  }

  /**
   * TrackBy function for *ngFor optimization
   */
  trackByRidId(index: number, rid: any): string {
    return rid.id;
  }
}
